import React, { useState, useEffect } from 'react';
import ModernLayout from '../components/modern_layout';
import ModernProductCard from '../components/modern_product_card';
import ModernLoader from '../components/modern_loader';
import { useGlobalToast } from '../components/modern_toast';
import usePageTitle from '../hooks/usePageTitle';
import { buscar_produto_detalhes } from '../services/api';

function ModernFavoritos() {
  usePageTitle('Meus Favoritos');
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToast } = useGlobalToast();

  const loadFavoriteProducts = async () => {
    try {
      setLoading(true);
      const favoriteIds = JSON.parse(localStorage.getItem('favoritos') || '[]');
      
      if (favoriteIds.length === 0) {
        setFavoriteProducts([]);
        setLoading(false);
        return;
      }

      console.log('📋 Loading favorite products for IDs:', favoriteIds);
      
      // Buscar detalhes dos produtos favoritos
      const productPromises = favoriteIds.map(async (produtoId) => {
        try {
          const response = await buscar_produto_detalhes({ 
            produto: produtoId,
            id_loja: sessionStorage.getItem('loja') || '486'
          });
          
          if (response && response.data && response.data.data) {
            // Transformar os dados da API para o formato esperado
            const produto = response.data.data;
            return {
              id: produtoId,
              nome: produto.DESC_PRODUTO || 'Produto sem nome',
              preco: produto.PRECO || 0,
              img: produto.IMAGEM || '',
              estoque: produto.ESTOQUE || 0,
              grupo: produto.GRUPO_PRODUTO || ''
            };
          }
          console.warn(`Produto ${produtoId} não encontrado`);
          return null;
        } catch (error) {
          console.error(`Erro ao buscar produto ${produtoId}:`, error);
          return null;
        }
      });

      const products = await Promise.all(productPromises);
      const validProducts = products.filter(product => product !== null);
      
      console.log('✅ Loaded favorite products:', validProducts.length);
      setFavoriteProducts(validProducts);
      
      if (validProducts.length < favoriteIds.length) {
        addToast('Alguns produtos favoritos não foram encontrados', 'warning');
      }
      
    } catch (error) {
      console.error('❌ Error loading favorites:', error);
      setError('Erro ao carregar produtos favoritos');
      addToast('Erro ao carregar favoritos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFavorites = async () => {
      try {
        setLoading(true);
        const favoriteIds = JSON.parse(localStorage.getItem('favoritos') || '[]');
        
        if (favoriteIds.length === 0) {
          setFavoriteProducts([]);
          setLoading(false);
          return;
        }

        console.log('📋 Loading favorite products for IDs:', favoriteIds);
        
        // Buscar detalhes dos produtos favoritos
        const productPromises = favoriteIds.map(async (produtoId) => {
          try {
            const response = await buscar_produto_detalhes({ 
              produto: produtoId,
              id_loja: sessionStorage.getItem('loja') || '486'
            });
            
            if (response && response.data && response.data.data) {
              // Transformar os dados da API para o formato esperado
              const produto = response.data.data;
              return {
                id: produtoId,
                nome: produto.DESC_PRODUTO || 'Produto sem nome',
                preco: produto.PRECO || 0,
                img: produto.IMAGEM || '',
                estoque: produto.ESTOQUE || 0,
                grupo: produto.GRUPO_PRODUTO || ''
              };
            }
            console.warn(`Produto ${produtoId} não encontrado`);
            return null;
          } catch (error) {
            console.error(`Erro ao buscar produto ${produtoId}:`, error);
            return null;
          }
        });

        const products = await Promise.all(productPromises);
        const validProducts = products.filter(product => product !== null);
        
        console.log('✅ Loaded favorite products:', validProducts.length);
        setFavoriteProducts(validProducts);
        
        if (validProducts.length < favoriteIds.length) {
          addToast('Alguns produtos favoritos não foram encontrados', 'warning');
        }
        
      } catch (error) {
        console.error('❌ Error loading favorites:', error);
        setError('Erro ao carregar produtos favoritos');
        addToast('Erro ao carregar favoritos', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [addToast]);

  const handleAddToCart = async (produto) => {
    // Implementar lógica de adicionar ao carrinho
    console.log('Adding to cart:', produto);
    addToast('Produto adicionado ao carrinho!', 'success');
  };

  const clearAllFavorites = () => {
    localStorage.setItem('favoritos', '[]');
    setFavoriteProducts([]);
    addToast('Todos os favoritos foram removidos', 'info');
  };

  if (loading) {
    return <ModernLoader message="Carregando seus favoritos..." />;
  }

  if (error) {
    return (
      <ModernLayout>
        <div className="container mt-4">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="text-center">
                <i className="bi bi-exclamation-triangle fs-1 text-warning mb-3"></i>
                <h4>Erro ao carregar favoritos</h4>
                <p className="text-muted">{error}</p>
                <button
                  className="btn btn-modern"
                  onClick={loadFavoriteProducts}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout>
    <div className="container mt-4">
      {/* Header da página */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold mb-1">
                <i className="bi bi-heart-fill text-danger me-2"></i>
                Meus Favoritos
              </h2>
              <p className="text-muted mb-0">
                {favoriteProducts.length === 0 
                  ? 'Nenhum produto favoritado ainda'
                  : `${favoriteProducts.length} produto${favoriteProducts.length !== 1 ? 's' : ''} favorito${favoriteProducts.length !== 1 ? 's' : ''}`
                }
              </p>
            </div>
            
            {favoriteProducts.length > 0 && (
              <button
                className="btn btn-outline-danger"
                onClick={clearAllFavorites}
              >
                <i className="bi bi-trash me-2"></i>
                Limpar todos
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lista de produtos favoritos */}
      {favoriteProducts.length === 0 ? (
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="text-center py-5">
              <i className="bi bi-heart fs-1 text-muted mb-3"></i>
              <h4>Nenhum favorito ainda</h4>
              <p className="text-muted mb-4">
                Explore nossos produtos e adicione seus favoritos clicando no ❤️
              </p>
              <a href="/" className="btn btn-modern">
                <i className="bi bi-house me-2"></i>
                Explorar produtos
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          {favoriteProducts.map((produto, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4">
              <ModernProductCard
                produto={produto}
                onAddToCart={handleAddToCart}
              />
            </div>
          ))}
        </div>
      )}

      {/* Dica de uso */}
      <div className="row mt-5">
        <div className="col-12">
          <div className="modern-card p-4 text-center">
            <h6 className="fw-bold mb-2">💡 Dica</h6>
            <p className="text-muted mb-0">
              Clique no ❤️ nos produtos para adicioná-los ou removê-los dos seus favoritos.
              Seus favoritos são salvos localmente no seu navegador.
            </p>
          </div>
        </div>
      </div>
    </div>
    </ModernLayout>
  );
}

export default ModernFavoritos;
